/// <amd-module name="Repos/Changes/Components/ChangeItemViewer" />
import * as React from "react";
import { ChangeDiffContent } from "Repos/Changes/Components/ChangeDiffContent";
import { ChangeFileContent } from "Repos/Changes/Components/ChangeFileContent";
import { ChangedFileViewMode } from "Repos/Changes/Contracts";
import { useChangesContext } from "Repos/ChangesCommon/ChangesContext";
import { DiffViewerType, IChange } from "Repos/ChangesCommon/Contracts";
import { VersionControlChangeType } from "Repos/Common/Contracts/Git";
import { DiffViewerOrientation } from "Repos/Common/Contracts/VersionControlViewModel";
import { getReposRoutingService } from "Repos/Common/ReposRoutingService";
import { getGitRepository } from "Repos/Common/VCViewModelDataProviderService";
import { IDiffMonacoDiscussionWidget } from "Repos/Discussion/Common/DiscussionContracts";
import { ContentPreview, MarkdownFilesHubUrlTransformer } from "Repos/Files/Components/ContentPreview";
import { getContentPreviewService } from "Repos/Files/ContentPreview";
import { IReadonlyObservableArray, IReadonlyObservableValue, ObservableCollection, ObservableLike, ObservableValue } from "VSS/Core/Observable";
import { ScreenSize } from "VSS/Core/Util/Screen";
import { IEditorWidgetRegistration } from "VSS/Features/Monaco/Contracts";
import { useComponentContext } from "VSS/Platform/Layout";
import { Observer } from "VSSUI/Observer";
import { ScreenSizeObserver } from "VSSUI/Utilities/ScreenSize";

export interface IChangeViewerProps<TVersionDescriptor> {
    /**
     * If true, keyboard shortcuts for next and previous difference will be added to Monaco
     */
    addDiffKeyboardShortcuts: boolean;

    /**
     * The base version to compare files against
     */
    compareVersion: TVersionDescriptor;

    /**
     * List of custom widget registrations for the file/diff content.
     */
    diffWidgets?: IEditorWidgetRegistration[] | IReadonlyObservableArray<IEditorWidgetRegistration>;

    /**
     * View mode when a file is selected
     */
    fileViewMode: IReadonlyObservableValue<ChangedFileViewMode>;

    /**
     * Gets the currently selected item (file or folder)
     */
    selectedItem: IChange;
}

export function ChangeItemViewer<TVersionDescriptor>(props: IChangeViewerProps<TVersionDescriptor>): JSX.Element | null {
    const { addDiffKeyboardShortcuts, compareVersion, diffWidgets, fileViewMode, selectedItem } = props;

    if (selectedItem.isFolder) {
        return null;
    }

    const { pageContext } = useComponentContext();
    const { filesService } = useChangesContext();

    return (
        <Observer fileViewMode={fileViewMode}>
            {({ fileViewMode }: { fileViewMode: ChangedFileViewMode }) => {
                let version = filesService.convertVersionToDescriptor(selectedItem.commitId);
                if ((selectedItem.changeType & VersionControlChangeType.Delete) === VersionControlChangeType.Delete) {
                    version = compareVersion;
                }

                if (fileViewMode === ChangedFileViewMode.Preview) {
                    const contentPreviewService = getContentPreviewService(pageContext);
                    const previewContribution = contentPreviewService.getPreviewContribution(selectedItem.path);
                    if (previewContribution) {
                        const urlTransformer = new MarkdownFilesHubUrlTransformer(
                            selectedItem.path,
                            false,
                            versionString => versionString === filesService.convertDescriptorToVersion(version),
                            (path, anchor) => {
                                const routingService = getReposRoutingService(pageContext);
                                const repository = getGitRepository(pageContext)!;
                                return routingService.getFilesUrl(repository.name, repository.project.name, path, version as any, anchor);
                            },
                            path => filesService.getFileDownloadUrl(path, version, false)
                        );

                        return (
                            <ContentPreview
                                contributionId={previewContribution.contributionId}
                                contribution={previewContribution}
                                getItemContent={() => filesService.getItemContent(selectedItem.path, version)}
                                urlTransformer={urlTransformer}
                            />
                        );
                    }
                }

                if (
                    !(selectedItem.changeType & VersionControlChangeType.Edit) ||
                    selectedItem.changeType & VersionControlChangeType.Add ||
                    fileViewMode === ChangedFileViewMode.ModifiedContent
                ) {
                    const isOriginalDiff = (selectedItem.changeType & VersionControlChangeType.Delete) === VersionControlChangeType.Delete;
                    const filterItem = (widget: IDiffMonacoDiscussionWidget) =>
                        widget.addToOriginalDiff === undefined || widget.addToOriginalDiff === isOriginalDiff ? widget : undefined;
                    const filterObservableWidgets = (observableArray: IReadonlyObservableArray<IDiffMonacoDiscussionWidget>) => {
                        const observableCollection = new ObservableCollection<IDiffMonacoDiscussionWidget>();
                        observableCollection.push(observableArray, filterItem);
                        return observableCollection;
                    };

                    const filteredDiffWidgets = ObservableLike.isObservable(diffWidgets)
                        ? filterObservableWidgets(diffWidgets as IReadonlyObservableArray<IDiffMonacoDiscussionWidget>)
                        : diffWidgets
                        ? (diffWidgets as Array<IDiffMonacoDiscussionWidget>).filter(filterItem)
                        : undefined;

                    return (
                        <ChangeFileContent
                            diffWidgets={filteredDiffWidgets}
                            fileDownloadUrl={filesService.getFileDownloadUrl(selectedItem.path, version)}
                            itemContent={filesService.getItemContent(selectedItem.path, version)}
                        />
                    );
                } else {
                    return (
                        <ScreenSizeObserver>
                            {(screenSizeProps: { screenSize: ScreenSize }) => (
                                <ChangeDiffContent
                                    addDiffKeyboardShortcuts={addDiffKeyboardShortcuts}
                                    compareVersion={compareVersion}
                                    diffViewerOrientation={
                                        screenSizeProps.screenSize <= ScreenSize.small
                                            ? new ObservableValue(DiffViewerOrientation.Inline)
                                            : filesService.getDiffOrientation(DiffViewerType.detail)
                                    }
                                    diffWidgets={diffWidgets}
                                    getFileDownloadUrl={(path, version) => filesService.getFileDownloadUrl(path, version)}
                                    isPixelDiff={fileViewMode === ChangedFileViewMode.PixelDiff}
                                    selectedItem={selectedItem}
                                    scrollToLine={new ObservableValue<number | undefined>(selectedItem.lineNumber)}
                                />
                            )}
                        </ScreenSizeObserver>
                    );
                }
            }}
        </Observer>
    );
}
